import React from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import Icon from "components/AppIcon";
import Button from "components/ui/Button";
import { useGetClientLedger } from "../useGetClientLedger";
import { useGetClientBalance } from "../useGetClientBalance";

const money = (value, locale = "en-SA") =>
  Number(value || 0).toLocaleString(locale, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
const getBalanceDisplay = (value) => {
  const amount = Number(value || 0);

  if (amount > 0) {
    return {
      labelKey: "clientLedger.balance.clientOwesYou",
      amount,
      color: "text-green-600 dark:text-green-400",
    };
  }

  if (amount < 0) {
    return {
      labelKey: "clientLedger.balance.youOweClient",
      amount: Math.abs(amount),
      color: "text-red-600 dark:text-red-400",
    };
  }

  return {
    labelKey: "clientLedger.balance.settled",
    amount: 0,
    color: "text-foreground",
  };
};

const entryLabel = (type, t) =>
  type
    ? t(`clientLedger.entryTypes.${type}`, { defaultValue: type })
    : t("clientLedger.entryTypes.fallback");

const ClientLedgerModal = ({ client, onClose }) => {
  const { t, i18n } = useTranslation("pages");
  const clientId = client?.id || client?._id;
  const locale = i18n.language === "ar" ? "ar-SA" : "en-SA";

  const {
    data: ledgerData,
    isLoading: isLedgerLoading,
    isError: isLedgerError,
  } = useGetClientLedger(clientId);

  const { data: balanceData, isLoading: isBalanceLoading } =
    useGetClientBalance(clientId);

  const rows = ledgerData?.ledger || [];
  const summary = ledgerData?.summary || {};
  const balance = balanceData?.balance || {};
  const currentBalanceValue = summary.balanceDue ?? balance.balanceDue ?? 0;
  const balanceDisplay = getBalanceDisplay(currentBalanceValue);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[9999] flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.96, opacity: 0, y: 16 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.96, opacity: 0, y: 16 }}
        className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-border w-full max-w-5xl max-h-[92vh] overflow-hidden flex flex-col"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="px-6 py-4 border-b border-border flex items-center justify-between bg-muted/30">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-primary/10 rounded-lg flex items-center justify-center">
              <Icon name="BookOpen" size={18} className="text-primary" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-foreground">
                {t("clientLedger.title")}
              </h2>
              <p className="text-xs text-muted-foreground mt-0.5">
                {t("clientLedger.subtitle", {
                  name: client?.name || t("clients.title"),
                })}
              </p>
            </div>
          </div>

          <button
            onClick={onClose}
            className="p-2 hover:bg-muted rounded-lg transition-colors"
          >
            <Icon name="X" size={18} className="text-muted-foreground" />
          </button>
        </div>

        <div className="p-6 overflow-y-auto flex-1 space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="rounded-xl border border-border bg-card p-4">
              <div className="text-xs text-muted-foreground mb-1">
                {t("clientLedger.debitTotal")}
              </div>
              <div className="text-lg font-semibold text-foreground">
                SR {money(summary.debitTotal ?? balance.debitTotal, locale)}
              </div>
            </div>

            <div className="rounded-xl border border-border bg-card p-4">
              <div className="text-xs text-muted-foreground mb-1">
                {t("clientLedger.creditTotal")}
              </div>
              <div className="text-lg font-semibold text-foreground">
                SR {money(summary.creditTotal ?? balance.creditTotal, locale)}
              </div>
            </div>

            <div className="rounded-xl border border-border bg-card p-4">
              <div className="text-xs text-muted-foreground mb-1">
                {t(balanceDisplay.labelKey)}
              </div>
              <div className={`text-lg font-semibold ${balanceDisplay.color}`}>
                SR {money(balanceDisplay.amount, locale)}
              </div>
            </div>
          </div>

          {isLedgerLoading || isBalanceLoading ? (
            <div className="rounded-xl border border-border p-8 text-center text-muted-foreground">
              {t("clientLedger.loading")}
            </div>
          ) : isLedgerError ? (
            <div className="rounded-xl border border-red-200 bg-red-50 dark:bg-red-900/10 p-4 text-sm text-red-700 dark:text-red-300">
              {t("clientLedger.failedLoad")}
            </div>
          ) : rows.length === 0 ? (
            <div className="rounded-xl border border-border p-8 text-center">
              <Icon
                name="FileText"
                size={32}
                className="mx-auto mb-3 text-muted-foreground"
              />
              <p className="text-muted-foreground">
                {t("clientLedger.empty")}
              </p>
            </div>
          ) : (
            <div className="rounded-xl border border-border overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-muted">
                    <tr>
                      <th className="px-4 py-3 text-start text-xs font-semibold text-foreground">
                        {t("clientLedger.table.date")}
                      </th>
                      <th className="px-4 py-3 text-start text-xs font-semibold text-foreground">
                        {t("clientLedger.table.type")}
                      </th>
                      <th className="px-4 py-3 text-start text-xs font-semibold text-foreground">
                        {t("clientLedger.table.reference")}
                      </th>
                      <th className="px-4 py-3 text-start text-xs font-semibold text-foreground">
                        {t("clientLedger.table.description")}
                      </th>
                      <th className="px-4 py-3 text-end text-xs font-semibold text-foreground">
                        {t("clientLedger.table.debit")}
                      </th>
                      <th className="px-4 py-3 text-end text-xs font-semibold text-foreground">
                        {t("clientLedger.table.credit")}
                      </th>
                      <th className="px-4 py-3 text-end text-xs font-semibold text-foreground">
                        {t("clientLedger.table.runningBalance")}
                      </th>
                    </tr>
                  </thead>

                  <tbody className="divide-y divide-border">
                    {rows.map((row) => (
                      <tr key={row.id}>
                        <td className="px-4 py-3 text-sm text-foreground">
                          {row.date
                            ? new Date(row.date).toLocaleDateString(locale)
                            : "—"}
                        </td>
                        <td className="px-4 py-3 text-sm text-foreground">
                          {entryLabel(row.entryType, t)}
                        </td>
                        <td className="px-4 py-3 text-sm font-mono text-foreground">
                          {row.sourceNumber || "—"}
                        </td>
                        <td className="px-4 py-3 text-sm text-muted-foreground max-w-[260px] truncate">
                          {row.description || "—"}
                        </td>
                        <td className="px-4 py-3 text-sm text-end text-foreground">
                          {row.debit ? `SR ${money(row.debit, locale)}` : "—"}
                        </td>
                        <td className="px-4 py-3 text-sm text-end text-foreground">
                          {row.credit ? `SR ${money(row.credit, locale)}` : "—"}
                        </td>
                        <td className="px-4 py-3 text-sm text-end font-semibold text-foreground">
                          SR {money(row.runningBalance, locale)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-border bg-muted/30 flex justify-end">
          <Button variant="outline" size="md" onClick={onClose}>
            {t("common:actions.close")}
          </Button>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default ClientLedgerModal;
